<template>
  <div class="sound-page">
    <section v-if="contentReady" class="notes-hero sound-hero">
      <div class="notes-hero-copy">
        <span class="notes-eyebrow">{{ intro.eyebrow }}</span>
        <h1>{{ intro.title }}</h1>
        <p v-if="intro.description">{{ intro.description }}</p>
        <div v-if="intro.tags.length" class="notes-hero-meta">
          <span v-for="tag in intro.tags" :key="tag">{{ tag }}</span>
        </div>
      </div>

      <div class="notes-hero-panel sound-hero-panel">
        <section class="sound-playlist-widget" :class="{ 'is-expanded': playlistExpanded }">
          <div class="sound-playlist-widget-head">
            <div>
              <span class="sound-playlist-widget-label">播放列表</span>
            </div>
            <div class="sound-playlist-widget-actions">
              <button
                type="button"
                class="sound-playlist-widget-button"
                @click="playlistExpanded = !playlistExpanded"
              >
                {{ playlistExpanded ? "收起" : "展开" }}
              </button>
            </div>
          </div>

          <div v-if="playlistExpanded" class="sound-playlist-widget-body">
            <div v-if="playlistTracks.length" class="sound-playlist-list">
              <article
                v-for="track in playlistTracks"
                :key="track.id"
                class="sound-playlist-item"
                :class="{ 'is-active': currentTrack?.id === track.id }"
              >
                <button
                  type="button"
                  class="sound-playlist-play"
                  @click="playTrack(track)"
                >
                  <span class="sound-playlist-copy">
                    <strong>{{ formatPlaylistTrackLabel(track) }}</strong>
                  </span>
                </button>
                <button
                  type="button"
                  class="sound-playlist-remove"
                  @click="removeTrackFromPlaylist(track.id)"
                >
                  删除
                </button>
              </article>
            </div>

            <div v-else class="sound-playlist-empty">
              在下面把歌曲加入列表后，这里会按顺序连续循环播放。
            </div>

            <div class="sound-playlist-footer">
              <button
                type="button"
                class="sound-playlist-action"
                :disabled="!playlistTracks.length"
                @click="playPlaylist"
              >
                播放列表
              </button>
              <button
                type="button"
                class="sound-playlist-clear"
                :disabled="!playlistTracks.length"
                @click="clearPlaylist"
              >
                清空列表
              </button>
            </div>
          </div>
        </section>
      </div>
    </section>

    <section v-if="contentReady" class="notes-toolbar sound-toolbar">
      <div class="notes-tags">
        <button
          type="button"
          :class="{ 'is-active': currentFilter === 'all' }"
          @click="setFilter('all')"
        >
          全部
        </button>
        <button
          v-for="category in categories"
          :key="category.slug"
          type="button"
          :class="{ 'is-active': currentFilter === category.slug }"
          @click="setFilter(category.slug)"
        >
          {{ category.title }}
        </button>
      </div>
    </section>

    <section class="sound-track-grid">
      <div v-if="!contentReady" class="notes-empty-state">
        <h2>内容加载中</h2>
        <p>正在从本地后台同步声音留白内容，请稍等一下。</p>
      </div>

      <div v-else-if="!filteredTracks.length" class="notes-empty-state">
        <h2>这里还没有歌曲</h2>
        <p>进入后台新增歌曲链接并保存后，这里就会出现可以播放的声音卡片。</p>
      </div>

      <div
        v-else
        v-for="track in filteredTracks"
        :key="track.id"
        class="sound-track-card"
        :class="{ 'is-active': currentTrack?.id === track.id }"
        role="button"
        tabindex="0"
        @click="playTrack(track)"
        @keydown.enter.prevent="playTrack(track)"
        @keydown.space.prevent="playTrack(track)"
      >
        <button
          type="button"
          class="sound-track-queue-button"
          :class="{ 'is-added': isTrackInPlaylist(track.id) }"
          :aria-label="isTrackInPlaylist(track.id)
            ? `将 ${track.title} 移出播放列表`
            : `将 ${track.title} 加入播放列表`"
          @click.stop="toggleTrackInPlaylist(track)"
        >
          {{ isTrackInPlaylist(track.id) ? "−" : "+" }}
        </button>
        <span class="sound-track-main">
          <strong>{{ track.title }}</strong>
          <span>{{ track.artist || track.categoryTitle || "声音留白" }}</span>
        </span>
        <span class="sound-track-meta">{{ track.duration || "音频作品" }}</span>
      </div>
    </section>

    <section class="sound-player-bar" :class="{ 'is-empty': !currentTrack }">
      <audio
        ref="audioRef"
        :src="currentAudioSrc"
        preload="metadata"
        @loadedmetadata="syncDuration"
        @timeupdate="syncCurrentTime"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        @ended="playNextTrack"
      ></audio>

      <div class="sound-player-now">
        <span class="sound-player-label">Now Playing</span>
        <strong>{{ currentTrack?.title || "选择一首歌曲" }}</strong>
        <span>{{ currentTrack?.artist || currentTrack?.categoryTitle || "声音留白" }}</span>
        <span class="sound-player-mode">
          {{ playlistTracks.length ? `播放列表循环 · ${playlistTracks.length} 首` : "当前分类循环播放" }}
        </span>
      </div>

      <button
        type="button"
        class="sound-player-button"
        :disabled="!currentTrack?.audioUrl"
        @click="togglePlayback"
      >
        {{ isPlaying ? "暂停" : "播放" }}
      </button>

      <div class="sound-lyric-panel">
        <strong class="sound-lyric-line sound-lyric-current">
          {{ lyricDisplay.current }}
        </strong>
      </div>

      <div class="sound-player-progress">
        <span>{{ formatTime(currentTime) }}</span>
        <input
          type="range"
          min="0"
          :max="duration || 0"
          step="0.1"
          :value="currentTime"
          :disabled="!duration"
          @input="seekTo($event.target.value)"
        >
        <span>{{ formatTime(duration) }}</span>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch, watchEffect } from "vue";
import { useContentStore } from "@/store/contentStore";

const PLAYLIST_STORAGE_KEY = "xfwebsite-sound-blank-playlist-v1";

const defaultIntro = {
  eyebrow: "声音留白 / Sound Blank",
  title: "这里存放我的音频作品",
  description: "把旋律和情绪放在这里，让声音有一处可以停靠的地方。",
  tags: ["歌曲播放", "链接入库", "底部控制", "持续更新"],
};

const audioRef = ref(null);
const currentFilter = ref("all");
const currentTrack = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const lyricLines = ref([]);
const lyricLoading = ref(false);
const lyricError = ref("");
const playlistTrackIds = ref([]);
const playlistExpanded = ref(false);

const { audioTracks, moduleIntros, contentReady } = useContentStore();

const intro = computed(() => moduleIntros.value.soundBlank || defaultIntro);

const categories = computed(() =>
  Array.from(
    new Map(
      audioTracks.value.map((item) => [
        item.categorySlug,
        { slug: item.categorySlug, title: item.categoryTitle },
      ])
    ).values()
  )
);

const filteredTracks = computed(() => {
  if (currentFilter.value === "all") {
    return audioTracks.value;
  }

  return audioTracks.value.filter((item) => item.categorySlug === currentFilter.value);
});

const playlistTracks = computed(() =>
  playlistTrackIds.value
    .map((trackId) => audioTracks.value.find((item) => item.id === trackId))
    .filter(Boolean)
);

const playbackTracks = computed(() => {
  const playlistPlayableTracks = playlistTracks.value.filter((track) => track.audioUrl);
  if (playlistPlayableTracks.length) {
    return playlistPlayableTracks;
  }

  return filteredTracks.value.filter((track) => track.audioUrl);
});

const getPlayableAudioUrl = (audioUrl = "") => {
  const source = String(audioUrl || "").trim();
  if (!source || source.startsWith("/") || source.startsWith("data:")) {
    return source;
  }

  if (/^https?:\/\//i.test(source)) {
    return `/api/audio-proxy?url=${encodeURIComponent(source)}`;
  }

  return source;
};

const currentAudioSrc = computed(() =>
  getPlayableAudioUrl(currentTrack.value?.audioUrl || "")
);

const currentLyricsSrc = computed(() =>
  getPlayableAudioUrl(currentTrack.value?.lyricsUrl || "")
);

const activeLyricIndex = computed(() => {
  if (!lyricLines.value.length) {
    return -1;
  }

  const index = lyricLines.value.findIndex(
    (line, lineIndex) =>
      currentTime.value >= line.time &&
      (
        lineIndex === lyricLines.value.length - 1 ||
        currentTime.value < lyricLines.value[lineIndex + 1].time
      )
  );

  if (index >= 0) {
    return index;
  }

  return currentTime.value < lyricLines.value[0].time ? 0 : lyricLines.value.length - 1;
});

const lyricDisplay = computed(() => {
  if (!currentTrack.value) {
    return {
      current: "选择一首歌曲，歌词会在这里跟随播放。",
    };
  }

  if (lyricLoading.value) {
    return {
      current: "歌词加载中...",
    };
  }

  if (lyricError.value) {
    return {
      current: lyricError.value,
    };
  }

  if (!currentLyricsSrc.value) {
    return {
      current: "这首歌还没有上传歌词。",
    };
  }

  if (!lyricLines.value.length) {
    return {
      current: "歌词文件为空，请重新上传包含歌词内容的 LRC 或 TXT 文件。",
    };
  }

  const index = Math.max(0, activeLyricIndex.value);
  return {
    current: lyricLines.value[index]?.text || "",
  };
});

const formatTime = (value) => {
  if (!Number.isFinite(value) || value <= 0) {
    return "00:00";
  }

  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const setFilter = (filter) => {
  currentFilter.value = filter;
};

const isTrackInPlaylist = (trackId) => playlistTrackIds.value.includes(trackId);

const savePlaylist = () => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(
    PLAYLIST_STORAGE_KEY,
    JSON.stringify(playlistTrackIds.value)
  );
};

const loadPlaylist = () => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    const raw = window.localStorage.getItem(PLAYLIST_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    playlistTrackIds.value = Array.isArray(parsed)
      ? parsed.map((item) => String(item || "")).filter(Boolean)
      : [];
  } catch (error) {
    playlistTrackIds.value = [];
  }
};

const toggleTrackInPlaylist = (track) => {
  if (!track?.id) {
    return;
  }

  if (isTrackInPlaylist(track.id)) {
    playlistTrackIds.value = playlistTrackIds.value.filter((item) => item !== track.id);
    return;
  }

  playlistTrackIds.value = [...playlistTrackIds.value, track.id];
};

const removeTrackFromPlaylist = (trackId) => {
  playlistTrackIds.value = playlistTrackIds.value.filter((item) => item !== trackId);
};

const clearPlaylist = () => {
  playlistTrackIds.value = [];
};

const playPlaylist = async () => {
  const firstPlayable = playlistTracks.value.find((track) => track.audioUrl);
  if (!firstPlayable) {
    return;
  }

  await playTrack(firstPlayable);
};

const formatPlaylistTrackLabel = (track) => {
  if (!track) {
    return "";
  }

  const artist = track.artist || track.categoryTitle || "声音留白";
  return `${track.title} · ${artist}`;
};

const resetProgress = () => {
  currentTime.value = 0;
  duration.value = 0;
};

const parseLyricTime = (value = "") => {
  const match = String(value).match(/^(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?$/);
  if (!match) {
    return null;
  }

  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  const fraction = match[3] ? Number(`0.${match[3].padEnd(3, "0").slice(0, 3)}`) : 0;
  return minutes * 60 + seconds + fraction;
};

const parseLyrics = (source = "") => {
  const lines = String(source || "").replace(/^\uFEFF/, "").replace(/\r\n/g, "\n").split("\n");
  const parsed = [];
  const plainText = [];

  lines.forEach((line) => {
    const timeMatches = [...line.matchAll(/\[(\d{1,2}:\d{2}(?:[.:]\d{1,3})?)\]/g)];
    const text = line.replace(/\[[^\]]+\]/g, "").trim();

    if (!timeMatches.length) {
      if (text) {
        plainText.push(text);
      }
      return;
    }

    timeMatches.forEach((match) => {
      const time = parseLyricTime(match[1]);
      if (time !== null && text) {
        parsed.push({ time, text });
      }
    });
  });

  if (parsed.length) {
    return parsed.sort((a, b) => a.time - b.time);
  }

  return plainText.slice(0, 18).map((text, index) => ({
    time: index * 5,
    text,
  }));
};

const withLyricsCacheBust = (lyricsUrl = "") => {
  const separator = String(lyricsUrl).includes("?") ? "&" : "?";
  return `${lyricsUrl}${separator}v=${Date.now()}`;
};

const loadLyrics = async (lyricsUrl = "") => {
  lyricLines.value = [];
  lyricError.value = "";

  if (!lyricsUrl) {
    return;
  }

  lyricLoading.value = true;

  try {
    const response = await fetch(withLyricsCacheBust(lyricsUrl), { cache: "no-store" });
    if (!response.ok) {
      throw new Error("歌词文件加载失败。");
    }

    const text = await response.text();
    lyricLines.value = parseLyrics(text);
  } catch (error) {
    lyricError.value = "歌词暂时无法加载。";
  } finally {
    lyricLoading.value = false;
  }
};

const playTrack = async (track) => {
  if (!track?.audioUrl) {
    return;
  }

  const isSameTrack = currentTrack.value?.id === track.id;
  currentTrack.value = track;

  await nextTick();

  if (!audioRef.value) {
    return;
  }

  if (!isSameTrack) {
    resetProgress();
    audioRef.value.load();
  }

  try {
    await audioRef.value.play();
  } catch (error) {
    isPlaying.value = false;
  }
};

const togglePlayback = async () => {
  if (!audioRef.value || !currentTrack.value?.audioUrl) {
    const firstPlayable = playbackTracks.value[0];
    if (firstPlayable) {
      await playTrack(firstPlayable);
    }
    return;
  }

  if (audioRef.value.paused) {
    try {
      await audioRef.value.play();
    } catch (error) {
      isPlaying.value = false;
    }
  } else {
    audioRef.value.pause();
  }
};

const seekTo = (value) => {
  const nextTime = Number(value);
  if (!audioRef.value || !Number.isFinite(nextTime)) {
    return;
  }

  audioRef.value.currentTime = nextTime;
  currentTime.value = nextTime;
};

const syncDuration = () => {
  duration.value = Number.isFinite(audioRef.value?.duration)
    ? audioRef.value.duration
    : 0;
};

const syncCurrentTime = () => {
  currentTime.value = audioRef.value?.currentTime || 0;
};

const playNextTrack = () => {
  if (!playbackTracks.value.length || !currentTrack.value) {
    isPlaying.value = false;
    return;
  }

  const currentIndex = playbackTracks.value.findIndex(
    (track) => track.id === currentTrack.value.id
  );
  const nextTrack = playbackTracks.value[
    currentIndex >= 0 ? (currentIndex + 1) % playbackTracks.value.length : 0
  ];

  playTrack(nextTrack);
};

watch(
  currentLyricsSrc,
  (lyricsUrl) => {
    loadLyrics(lyricsUrl);
  },
  { immediate: true }
);

watch(
  playlistTrackIds,
  () => {
    savePlaylist();
  },
  { deep: true }
);

watch(
  audioTracks,
  () => {
    const validTrackIds = new Set(audioTracks.value.map((track) => track.id));
    playlistTrackIds.value = playlistTrackIds.value.filter((trackId) =>
      validTrackIds.has(trackId)
    );

    if (
      currentTrack.value &&
      !audioTracks.value.some((track) => track.id === currentTrack.value.id)
    ) {
      currentTrack.value = null;
      audioRef.value?.pause();
      resetProgress();
    }
  },
  { deep: true, immediate: true }
);

onMounted(() => {
  loadPlaylist();
});

watchEffect(() => {
  document.title = "圣域空间 | 声音留白";
});
</script>
